// @flow

import React from "react";
import classNames from "classnames";
import Ajv from "ajv";

import { TooltipDropdown, View, Text } from "@components";
import Icons from "../icons";
import { removeNullAndUndefinedProps } from "./utils";
import type { SchemaTableSchemaDataProp } from "./types";
import styles from "./schema-table.scss";
import type { SchemaTableWidgetProps } from "./types";
import widgets from "./widgets";
import { v4 as uuid } from "uuid";
// SchemaTableErrors holds schema validation errors indexed by row and property
// name, the object structure is optimised for rendering
export type SchemaTableErrors = $Exact<{
  [number]: {
    [string]: Object,
  },
}>;

// SchemaTablApi is the public api, exposed to consumers via onRef callback.
// Allows consumer to programmatically trigger schema validation i.e. from
// submit button.
export type SchemaTableApi = $Exact<{
  // getData return a snapshot of the tableData at a point in time
  getData: () => Array<Object>,
  // getErrors returns a snapshot of errors, only populated after calling validate
  getErrors: () => ?SchemaTableErrors,
  // validate is a function call used to initial table wide validation
  validate: () => boolean,
  // reset is clear all visual validation errors
  reset: () => void,
}>;

export type SchemaTableProps = $Exact<{
  // onRef callback is used to provide consumers with a api object bound to this
  // instance, but importantly not direct access to this instance
  onRef: (instance: SchemaTableApi) => void,
  // tableData is the core data object passed to initialise the table and
  // provide updates from state
  tableData: Array<Object>,
  // schema is a JsonSchema object which will be used to enforce the shape of
  // the object, and any validation criteria
  schema: Object,
  // uiSchema is a SchemaTable specific object used to control how the table
  uiSchema: Object,
  // onChange emits after every internal update to the core table data
  onChange: (tableData: Array<Object>, isValid: boolean) => void,
  // disabled is used disable user input and provide a readonly version
  disabled: boolean,
  // activeOnHover is discouraged but can be used allow inline editing when the
  // user hovers over the row.  This can lead to issues with tab indexes etc.
  activeOnHover: boolean,
  // allowDeletes when true will show the delete row button to the right of the
  // table
  allowDeletes: boolean,
  // allowDeletesFn can be used to enable / disable individual delete buttons
  // on a per row basis
  allowDeletesFn: (item: Object, rowIndex: number) => boolean,
  // allowReordering when true will show the up down arrow button to the right
  // of the table
  allowReordering: boolean,
  // allowReoderingFn can be used to enable / disable individual up down button
  // arrays, on a per row basis
  allowReorderingFn: (item: Object, rowIndex: number) => boolean,
  // isHighlighted is callback triggered during the rendering phrase to
  // determine whether the row shoud be treated as highlighted or not i.e.
  // dusty orange background color
  isHighlighted: (item: Object, rowIndex: number) => boolean,
  // rowBackgroundColor callback triggered during the rendering phrase to allow
  // custom background colours to be applied to rows, on row by row basis, where
  // the logic is custom to the implementation.  Use this function when you need
  // to provide more colour options than the built in highlighted state. Expects
  // a string to be returned, which can be any valid css colour hex, rgba, named
  // etc. as long as it's a string representation.
  rowBackgroundColor?: (item: Object, rowIndex: number) => string,
}>;

type SchemaTableState = $Exact<{
  // activeId is the current active (i.e. onhover) row identifier
  activeId: ?number,
  // tableDate is the components internal representation of the tableData,
  // maintained directly through input actions and emitted onChange
  tableData: Array<Object>,
  // errors is a collection of errors objects with row and prop refs
  errors: ?SchemaTableErrors,
  // showErrors is used to set errors onChange only after the user has requested
  // table validation i.e. api.validate()
  showErrors: boolean,
  // touched indicates whether the user has interacted with the schema table, it
  // actually triggered from the onChange handler but it's intention is to pass
  // a master touched prop to children
  touched: boolean,
}>;

export class SchemaTable extends React.Component<SchemaTableProps, SchemaTableState> {
  static defaultProps = {
    onRef: () => undefined,
    onChange: () => undefined,
    disabled: false,
    activeOnHover: false,
    allowDeletes: false,
    allowDeletesFn: () => true,
    allowReordering: false,
    allowReorderingFn: () => true,
    isHighlighted: () => false,
  };

  state = {
    activeId: undefined,
    tableData: this.props.tableData,
    errors: null,
    showErrors: false,
    touched: false,
  };

  componentDidMount() {
    this.props.onRef(this.publicApi);
  }

  componentDidUpdate(prevProps: SchemaTableProps) {
    if (this.props.tableData && this.props.tableData !== prevProps.tableData) {
      this.setState({ tableData: this.props.tableData });
    }

    if (this.props.onRef !== prevProps.onRef) {
      this.props.onRef(this.publicApi);
    }
  }

  get publicApi() {
    return {
      getData: () => [...this.state.tableData],
      getErrors: () => (this.state.errors ? { ...this.state.errors } : null),
      validate: () => {
        this.setState({ showErrors: true });
        return this.isValid(this.data, true);
      },
      reset: () => {
        this.setState({ errors: null, showErrors: false, touched: false });
      },
    };
  }

  get data() {
    return this.state.tableData;
  }

  get properties(): Array<SchemaTableSchemaDataProp> {
    const uiOrder = this.props.uiSchema["ui:order"] || [];
    const uiProps = Object.keys(this.props.uiSchema).filter(
      name => !name.startsWith("ui:") && uiOrder.indexOf(name) === -1,
    );
    const orderedProps = [...uiOrder, ...uiProps];

    return orderedProps.map((name: string) => {
      const schema = this.props.schema.properties[name] || {};
      const ui = this.props.uiSchema[name] || {};

      // we need an option to not show table header at all
      // we can pass ui:title as null in ui schema in case we don't need it
      const title = ui["ui:title"] !== null ? ui["ui:title"] || schema.title || name : null;
      const header = ui["ui:header"];
      return {
        ...schema,
        name,
        required: (this.props.schema.required || []).indexOf(name) === -1,
        type: schema.type || "string",
        format: schema.format || "string",
        title,
        widget: ui["ui:widget"],
        options: {
          ...ui["ui:options"],
          ...(ui["ui:options"]
            ? {
                minimum: ui["ui:options"].minimum || schema.minimum,
                maximum: ui["ui:options"].maximum || schema.maximum,
                minLength: ui["ui:options"].minLength || schema.minLength,
                maxLength: ui["ui:options"].maxLength || schema.maxLength,
                header: ui["ui:header"],
              }
            : {}),
        },
        oneOf:
          (ui["ui:options"] && ui["ui:options"].oneOf) ||
          schema.oneOf ||
          ((schema.properties || {}).id || {}).oneOf,
      };
    });
  }

  handleMouseMove = (e: *) => {
    if (this.state.activeId !== +e.currentTarget.getAttribute("data-id")) {
      this.setState({ activeId: +e.currentTarget.getAttribute("data-id") });
    }
  };

  handleMouseLeave = (e: *) => {
    this.setState({ activeId: null });
  };

  isValid = (data: Array<Object>, updateErrors?: boolean) => {
    const validator = new Ajv({ allErrors: true }).compile({
      type: "array",
      items: this.props.schema,
    });

    const valid = validator(data.map(removeNullAndUndefinedProps));
    if (valid) {
      this.setState({ errors: null });
      return true;
    }

    let errors = null;
    if (updateErrors) {
      errors = validator.errors.reduce((acc: SchemaTableErrors, error: Object) => {
        const errorPathSplits = error.dataPath
          .replace("[", "")
          .replace("].", " ")
          .split(" ");
        return {
          ...acc,
          [+errorPathSplits[0]]: {
            ...(acc[+errorPathSplits[0]] ? acc[+errorPathSplits[0]] : {}),
            [errorPathSplits[1]]: error,
          },
        };
      }, {});
    }

    this.setState({ errors });
    return false;
  };

  handleNext = (next: Array<Object>) => {
    this.props.onChange(next, this.isValid(next, this.state.showErrors));

    this.setState({ tableData: next, activeId: null, touched: true });
  };

  update = (rowIndex: number, column: SchemaTableSchemaDataProp) => (value: any) => {
    const next = this.data.map((row: *, rIndex: number) =>
      rIndex === rowIndex ? { ...row, [column.name]: value } : row,
    );

    this.handleNext(next);
  };

  handleRowUp = (rowIndex: number) => () => {
    const next = this.data.slice(0);
    next.splice(rowIndex - 1, 0, next.splice(rowIndex, 1)[0]);

    this.handleNext(next);
  };

  handleRowDown = (rowIndex: number) => () => {
    const next = this.data.slice(0);
    next.splice(rowIndex + 1, 0, next.splice(rowIndex, 1)[0]);

    this.handleNext(next);
  };

  handleDeleteRow = (rowIndex: number) => () => {
    const next = this.data.slice(0);
    next.splice(rowIndex, 1);

    this.handleNext(next);
  };

  render() {
    return (
      <div className="schema-table__table" onMouseLeave={this.handleMouseLeave}>
        <div className="schema-table__table-row">
          {this.properties.map((prop: SchemaTableSchemaDataProp) => {
            const { width, minWidth, maxWidth, header } = prop.options;

            if (header)
              return (
                <Text lineHeight="3rem" type="h5">
                  {header}
                </Text>
              );

            if (!prop.title) {
              return null;
            }

            return (
              <div
                key={prop.name}
                className="schema-table__table-head"
                style={{ width, minWidth, maxWidth }}
              >
                {prop.options.tooltip ? (
                  <TooltipDropdown {...prop.options.tooltip}>{prop.title}</TooltipDropdown>
                ) : (
                  prop.title
                )}
                {prop.options && prop.options.required === true && (
                  <span className={"text-warning"}>*</span>
                )}
              </div>
            );
          })}
          {!this.props.disabled && this.props.allowReordering ? (
            <div className="schema-table__row-reorder" />
          ) : null}
          {!this.props.disabled && this.props.allowDeletes ? (
            <div className="schema-table__row-delete" />
          ) : null}
        </div>

        {this.data &&
          this.data.map &&
          this.data.map((row: Object, rowIndex: number) => {
            const id = rowIndex;
            const allowItemDelete = this.props.allowDeletesFn(row, rowIndex);
            const allowItemReordering = this.props.allowReorderingFn(row, rowIndex);
            const isHighlighted = this.props.isHighlighted(row, rowIndex);

            return (
              <div
                className={classNames("schema-table__table-row", {
                  "schema-table__table-row--active":
                    !this.props.disabled && this.state.activeId === id,
                  "schema-table__table-row--highlighted": !this.props.disabled && isHighlighted,
                })}
                style={{
                  ...(this.props.rowBackgroundColor
                    ? {
                        backgroundColor: this.props.rowBackgroundColor(row, rowIndex),
                      }
                    : {}),
                }}
                key={id}
                data-id={id}
                onMouseMove={this.handleMouseMove}
              >
                {this.properties.map((prop: SchemaTableSchemaDataProp) => {
                  const { width, minWidth, maxWidth } = prop.options;
                  let cellError = null;
                  if (this.state.errors) {
                    cellError = (this.state.errors[rowIndex] || {})[prop.name];
                  }
                  const { isValid } = prop.options;
                  if (isValid) {
                    cellError = !isValid(row, rowIndex);
                  }

                  return (
                    <div
                      key={prop.name}
                      className="schema-table__table-data"
                      style={{ width, minWidth, maxWidth }}
                    >
                      <SchemaTableCell
                        {...prop}
                        id={id}
                        row={row}
                        active={
                          !this.props.disabled &&
                          (!this.props.activeOnHover || this.state.activeId === id)
                        }
                        data={row[prop.name]}
                        error={cellError}
                        touched={this.state.touched}
                        onChange={this.update(rowIndex, prop)}
                      />
                    </div>
                  );
                })}
                {!this.props.disabled && this.props.allowReordering ? (
                  <div className="schema-table__row-reorder">
                    <div className="schema-table__row-reorder--up">
                      <span
                        className={`material-icons font-size-30 pointer text-color-${
                          rowIndex !== 0 && allowItemReordering ? "black" : "disabled"
                        }`}
                        onClick={
                          rowIndex !== 0 && allowItemReordering
                            ? this.handleRowUp(rowIndex)
                            : undefined
                        }
                      >
                        arrow_back
                      </span>
                    </div>
                    <div className="schema-table__row-reorder--down">
                      <span
                        className={`material-icons font-size-30 pointer text-color-${
                          rowIndex !== this.data.length - 1 && allowItemReordering
                            ? "black"
                            : "disabled"
                        }`}
                        onClick={
                          rowIndex !== this.data.length - 1 && allowItemReordering
                            ? this.handleRowDown(rowIndex)
                            : undefined
                        }
                      >
                        arrow_back
                      </span>
                    </div>
                  </div>
                ) : null}
                {!this.props.disabled && this.props.allowDeletes ? (
                  <div className="schema-table__row-delete">
                    <Icons.CloseIcon
                      color={allowItemDelete ? "charcoal-dark" : "disabled"}
                      onClick={allowItemDelete ? this.handleDeleteRow(rowIndex) : undefined}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
      </div>
    );
  }
}

function resolveDisplayValue(props: SchemaTableWidgetProps<any>) {
  let displayValue = props.data;

  if (typeof props.data === "object" && props.data instanceof Date) {
    displayValue = props.data.toString();
  }

  let oneOfConst = displayValue;
  if (props.type === "object") {
    oneOfConst = (props.data || {}).id;
  }

  if (props.oneOf) {
    displayValue = (props.oneOf.find(o => o.const === oneOfConst) || {}).title;
  }

  return displayValue;
}

export function SchemaTableCell(props: SchemaTableWidgetProps<any>) {
  if (props.widget && widgets[props.widget]) {
    const Widget = widgets[props.widget];

    const { editable } = props.options || {};
    const active = props.active && (editable === undefined || editable);
    const id = uuid();

    if (Widget) {
      return (
        <Widget
          {...props}
          id={id}
          row={props.row}
          data={props.data}
          active={active}
          onChange={props.onChange}
        />
      );
    }
  }

  return <div>{resolveDisplayValue(props)}</div>;
}

export default SchemaTable;
